<template>
    <div class="dynamic-height">
        <!-- Custom Questions -->
        <h4 class="text-subtitle-1 font-weight-bold mb-3">Custom Questions <span class="required">*</span></h4>

        <div v-for="(q, index) in customQuestions" :key="index" class="question-box mb-4">
            <div class="question-header">
                <span class="question-type">{{ formatType(q.type) }}</span>
                <div class="d-flex align-center gap-2">

                    <div style="background-color: #F2F2F2;border-radius: 8px;"
                        class="pa-2 d-flex justify-center align-center">
                        <DeleteBtn @click="removeQuestion(index)" />
                    </div>

                </div>
            </div>

            <v-text-field v-model="q.label" placeholder="Enter question" density="comfortable" variant="outlined"
                class="mb-2" />
            <div v-if="q.type === 'multiple choice'">
                <div v-for="(opt, i) in q.options" :key="i"
                    class="d-flex justify-space-between align-center mb-2 gap-2">
                    <div class="d-flex align-center flex-grow-1 gap-2">
                        <span>{{ i + 1 }} - </span>
                        <input v-model="q.options[i]" class="editable-option flex-grow-1 mx-2" placeholder="Option" />
                    </div>

                    <DeleteBtn @click="removeOption(q, i)" v-if="q.options.length > 1" />

                </div>

                <div v-if="q.newOptionInput" class="d-flex align-center gap-2 mb-2">
                    <span>{{ q.options.length + 1 }}.</span>
                    <v-text-field v-model="q.newOptionValue" density="compact" variant="underlined" class="flex-grow-1"
                        hide-details @blur="confirmNewOption(q)" @keyup.enter="confirmNewOption(q)"
                        placeholder="Add option" autofocus />
                </div>

                <v-btn v-else variant="text" size="small" class="mt-1 text-primary text-capitalize font-weight-bold"
                    @click="addOption(q)">
                    Add option
                </v-btn>
            </div>
        </div>

        <!-- Add Question -->
        <v-menu>
            <template #activator="{ props }">
                <v-btn v-bind="props" color="#237276" variant="outlined" class="mb-6 text-capitalize">
                    Add Question
                    <v-icon end>mdi-menu-down</v-icon>
                </v-btn>
            </template>
            <v-list>
                <v-list-item @click="addQuestion('multiple choice')">
                    Multiple Choice
                </v-list-item>
                <v-list-item @click="addQuestion('short answer')">
                    Short Answer
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Personal Information -->
        <h4 class="text-subtitle-1 font-weight-bold mb-2">
            Personal Information <span class="text-error">*</span>
        </h4>

        <v-list class="mb-6">

            <div v-for="(field, index) in personalFields" :key="field.name + '-' + index"
                class="personal-field-box mb-2 pa-0 d-flex align-center gap-2">
                <input v-model="field.value" :placeholder="field.name" class="flex-grow-1"
                    :type="getInputType(field.name)" :pattern="getPattern(field.name)" readonly />
                <DeleteBtn @click="removePersonalField(index)" />

            </div>

        </v-list>

        <v-menu class="mb-6" transition="scale-transition">
            <template #activator="{ props }">
                <v-btn v-bind="props" color="#237276" variant="outlined">
                    Add Field
                    <v-icon end>mdi-menu-down</v-icon>
                </v-btn>
            </template>

            <v-list>
                <!-- Contact Fields -->
                <v-list-group value="Contact fields" prepend-icon="mdi-account-box">
                    <template #activator="{ props }">
                        <v-list-item v-bind="props" title="Contact fields" />
                    </template>
                    <v-list-item @click="addPersonalField('Email')">Email</v-list-item>
                    <v-list-item @click="addPersonalField('Phone number')">Phone number</v-list-item>
                    <v-list-item @click="addPersonalField('WhatsApp number')">WhatsApp number</v-list-item>
                    <v-list-item @click="addPersonalField('Street address')">Street address</v-list-item>
                    <v-list-item @click="addPersonalField('City')">City</v-list-item>
                    <v-list-item @click="addPersonalField('State')">State</v-list-item>
                    <v-list-item @click="addPersonalField('Province')">Province</v-list-item>
                    <v-list-item @click="addPersonalField('Country')">Country</v-list-item>
                    <v-list-item @click="addPersonalField('Post code')">Post code</v-list-item>
                    <v-list-item @click="addPersonalField('Zip code')">Zip code</v-list-item>
                </v-list-group>

                <!-- User Info -->
                <v-list-group value="User information" prepend-icon="mdi-account">
                    <template #activator="{ props }">
                        <v-list-item v-bind="props" title="User information" />
                    </template>
                    <v-list-item @click="addPersonalField('Full Name')">Full Name</v-list-item>
                    <v-list-item @click="addPersonalField('First Name')">First Name</v-list-item>
                    <v-list-item @click="addPersonalField('Last Name')">Last Name</v-list-item>
                </v-list-group>

                <!-- Demographic -->
                <v-list-group value="Demographic questions" prepend-icon="mdi-account-group">
                    <template #activator="{ props }">
                        <v-list-item v-bind="props" title="Demographic questions" />
                    </template>
                    <v-list-item @click="addPersonalField('Date of Birth')">Date of Birth</v-list-item>
                    <v-list-item @click="addPersonalField('Gender')">Gender</v-list-item>
                    <v-list-item @click="addPersonalField('Marital Status')">Marital Status</v-list-item>
                    <v-list-item @click="addPersonalField('Relationship status')">Relationship status</v-list-item>
                    <v-list-item @click="addPersonalField('Military status')">Military status</v-list-item>
                </v-list-group>

                <!-- Work Info -->
                <v-list-group value="Work information" prepend-icon="mdi-briefcase-outline">
                    <template #activator="{ props }">
                        <v-list-item v-bind="props" title="Work information" />
                    </template>
                    <v-list-item @click="addPersonalField('Job Title')">Job Title</v-list-item>
                    <v-list-item @click="addPersonalField('Work Email')">Work Email</v-list-item>
                    <v-list-item @click="addPersonalField('Work Phone Number')">Work Phone Number</v-list-item>
                    <v-list-item @click="addPersonalField('Company name')">Company name</v-list-item>
                </v-list-group>

                <!-- National ID -->
                <v-list-group value="National ID number" prepend-icon="mdi-card-account-details-outline">
                    <template #activator="{ props }">
                        <v-list-item v-bind="props" title="National ID number" />
                    </template>
                    <v-list-item @click="addPersonalField('CPF (Brazil)')">CPF (Brazil)</v-list-item>
                    <v-list-item @click="addPersonalField('DNI (Argentina)')">DNI (Argentina)</v-list-item>
                    <v-list-item @click="addPersonalField('DNI (Peru)')">DNI (Peru)</v-list-item>
                    <v-list-item @click="addPersonalField('RUT (Chile)')">RUT (Chile)</v-list-item>
                    <v-list-item @click="addPersonalField('CC (Colombia)')">CC (Colombia)</v-list-item>
                    <v-list-item @click="addPersonalField('CI (Ecuador)')">CI (Ecuador)</v-list-item>
                    <v-list-item @click="addPersonalField('RFC (Mexico)')">RFC (Mexico)</v-list-item>
                </v-list-group>
            </v-list>
        </v-menu>
        <!-- Errors -->
        <div v-if="error.length" class="error-list mb-4 mt-6">
            <p v-for="(err, index) in error" :key="index" class="text-error">⚠ {{ err }}</p>
        </div>
    </div>
</template>

<script>
import { useSuperLeedForm } from "@/store/SuperLeedForm";
import DeleteBtn from '../../icons/DeleteBtn.vue';

export default {
    name: 'QuestionsStep',
    props: {
        platform: String,
    },
    components: {
        DeleteBtn,
    },
    computed: {
        store() {
            return useSuperLeedForm();
        },
        customQuestions: {
            get() {
                return this.store.MetaLeedQuestions.customQuestions;
            },
            set(value) {
                this.store.MetaLeedQuestions.customQuestions = value;
            },
        },
        personalFields: {
            get() {
                return this.store.MetaLeedQuestions.personalFields;
            },
            set(value) {
                this.store.MetaLeedQuestions.personalFields = value;
            },
        },
    },
    data() {
        return {
            error: [],
            fieldErrors: {},
            fieldTypeMap: {
                'Email': 'EMAIL',
                'Phone number': 'PHONE_NUMBER',
                'WhatsApp number': 'WHATSAPP_NUMBER',
                'Street address': 'STREET_ADDRESS',
                'City': 'CITY',
                'State': 'STATE',
                'Province': 'PROVINCE',
                'Country': 'COUNTRY',
                'Post code': 'POST_CODE',
                'Zip code': 'ZIP',

                'Full Name': 'FULL_NAME',
                'First Name': 'FIRST_NAME',
                'Last Name': 'LAST_NAME',

                'Date of Birth': 'DOB',
                'Gender': 'GENDER',
                'Marital Status': 'MARITIAL_STATUS',
                'Relationship status': 'RELATIONSHIP_STATUS',
                'Military status': 'MILITARY_STATUS',

                'Job Title': 'JOB_TITLE',
                'Work Phone Number': 'WORK_PHONE_NUMBER',
                'Work Email': 'WORK_EMAIL',
                'Company name': 'COMPANY_NAME',

                'CPF (Brazil)': 'ID_CPF',
                'DNI (Argentina)': 'ID_AR_DNI',
                'DNI (Peru)': 'ID_PE_DNI',
                'RUT (Chile)': 'ID_CL_RUT',
                'CC (Colombia)': 'ID_CO_CC',
                'CI (Ecuador)': 'ID_EC_CI',
                'RFC (Mexico)': 'ID_MX_RFC',
            }
        };
    },
    methods: {
        validate() {
            this.error = [];

            // 1. تحقق من وجود سؤال واحد على الأقل
            if (!this.customQuestions.length) {
                this.error.push("At least one custom question is required.");
            }

            // 2. تحقق من كل سؤال
            this.customQuestions.forEach((q, index) => {
                if (!q.label || q.label.trim() === "") {
                    this.error.push(`Question ${index + 1} is missing a label.`);
                }

                if (q.type === 'multiple choice') {
                    const filledOptions = Array.isArray(q.options) ? q.options.filter(opt => opt && opt.trim() !== "") : [];

                    if (filledOptions.length < 2) {
                        this.error.push(`Question ${index + 1} must have at least 2 options.`);
                    }
                }

            });

            // 3. تحقق من وجود بيانات شخصية واحدة على الأقل
            if (!this.personalFields.length) {
                this.error.push("At least one personal information field is required.");
            }

            return this.error.length === 0;
        },
        getInputType(name) {
            if (name === 'Email') return 'email';
            if (name === 'Phone Number') return 'tel';
            return 'text';
        },

        getPattern(name) {
            if (name === 'Email') return "[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$";
            if (name === 'Phone Number') return "[0-9]*";
            return null;
        },

        validateField(field) {
            const value = field.value.trim();
            if (field.name === 'Email') {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                this.fieldErrors[field.name] = emailRegex.test(value) ? '' : 'Invalid email';
            } else if (field.name === 'Phone Number') {
                const phoneRegex = /^[0-9]+$/;
                this.fieldErrors[field.name] = phoneRegex.test(value) ? '' : 'Only numbers allowed';
            } else {
                this.fieldErrors[field.name] = value.length > 0 ? '' : 'Required';
            }
        },

        addQuestion(type) {
            const questions = [...this.customQuestions];

            if (type === 'multiple choice') {
                questions.push({
                    type: 'multiple choice',
                    label: '',
                    options: ['Option 1'],
                    newOptionInput: false,
                    newOptionValue: '',
                });
            } else if (type === 'short answer') {
                questions.push({
                    type: 'short answer',
                    label: '',
                });
            }

            this.customQuestions = questions;
        },

        removeQuestion(index) {
            const questions = [...this.customQuestions];
            questions.splice(index, 1);
            this.customQuestions = questions;
        },

        addOption(question) {
            question.newOptionInput = true;
            question.newOptionValue = '';
        },

        confirmNewOption(question) {
            const value = question.newOptionValue.trim();
            if (value) {
                question.options.push(value);
            }
            question.newOptionInput = false;
            question.newOptionValue = '';
        },

        removeOption(question, i) {
            question.options.splice(i, 1);
        },

        addPersonalField(fieldName) {
            const exists = this.personalFields.find(f => f.name === fieldName);
            if (!exists) {
                const type = this.fieldTypeMap[fieldName] || 'CUSTOM';
                const updated = [
                    ...this.personalFields,
                    {
                        name: fieldName,
                        value: '',
                        type: type
                    }
                ];
                this.personalFields = updated;
            }
        },

        removePersonalField(index) {
            const updated = [...this.personalFields];
            updated.splice(index, 1);
            this.personalFields = updated;
        },

        formatType(type) {
            return type === 'multiple choice' ? 'Multiple choice' : 'Short answer';
        },
    },
};
</script>


<style scoped>
.text-error {
    color: #e53935 !important;
    font-size: 15px;
    margin-top: 6px;
}
.question-box {
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 16px;
    background-color: #fff;
}

.question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.question-type {
    font-weight: 600;
    color: #237276;
    font-size: 14px;
}

.optional-label span {
    font-size: 13px;
    color: #666;
}

.dynamic-height {
    max-height: 70vh;
}

.personal-field-box {
    border: 1px solid #dcdcdc;
    border-radius: 8px;
    padding: 6px 12px !important;
    background-color: #fff;
    transition: all 0.3s ease;
}

.personal-field-box:hover {
    border-color: #237276;
}



.personal-field-box input {
    border: none;
    border-radius: 8px;
    padding: 10px;
    width: 100%;
    background-color: #fff;
    transition: border-color 0.3s ease;
    font-size: 14px;
}

.personal-field-box input:focus {
    outline: none;
    border-color: none;
    box-shadow: none;
}

input:invalid {
    border: 1px solid red;
}

.editable-option {
    border: 1px solid transparent;
    padding: 6px 10px;
    font-size: 14px;
    outline: none;
    min-width: 200px;
    background-color: #fff;
    transition: border-color 0.3s;
}

.editable-option:focus {
    border-color: #237276;
    border: 1px solid #ccc;
}
</style>
